
import { useState } from 'react';

export function EligibilityComparison() {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [allExpanded, setAllExpanded] = useState(false);

  const cities = [
    {
      name: 'Vancouver',
      province: 'BC',
      entireHomeNights: '365 nights',
      privateRoomNights: '365 nights',
      limitType: 'Principal Residence',
      notes: 'No night cap. Must prove principal residence with utility bills and documentation.',
      link: '/vancouver-short-term-rental-rules'
    },
    {
      name: 'North Vancouver',
      province: 'BC',
      entireHomeNights: '182 nights',
      privateRoomNights: '365 nights',
      limitType: 'Principal Residence',
      notes: 'Must occupy 183+ days/year',
      link: '/north-vancouver-str-bylaws'
    },
    {
      name: 'Burnaby',
      province: 'BC',
      entireHomeNights: '28 nights',
      privateRoomNights: '90 nights',
      limitType: 'Calendar Cap',
      notes: 'Strict limits on entire unit rentals',
      link: '/burnaby-str-regulations'
    },
    {
      name: 'Surrey',
      province: 'BC',
      entireHomeNights: '182 nights',
      privateRoomNights: '365 nights',
      limitType: 'Principal Residence',
      notes: 'Must occupy 183+ days/year',
      link: '/surrey-rental-regulations'
    },
    {
      name: 'Langley',
      province: 'BC',
      entireHomeNights: '182 nights',
      privateRoomNights: '365 nights',
      limitType: 'Principal Residence',
      notes: 'Must occupy 183+ days/year',
      link: '/langley-str-licensing'
    },
    {
      name: 'Victoria',
      province: 'BC',
      entireHomeNights: '160 nights',
      privateRoomNights: '365 nights',
      limitType: 'Calendar Cap',
      notes: 'Fixed annual limit',
      link: '/victoria-str-regulations'
    },
    {
      name: 'Whistler',
      province: 'BC',
      entireHomeNights: '365 nights',
      privateRoomNights: '365 nights',
      limitType: 'No Cap',
      notes: 'Resort municipality with flexible rules',
      link: '/whistler-str-regulations'
    },
    {
      name: 'Calgary',
      province: 'AB',
      entireHomeNights: '365 nights',
      privateRoomNights: '365 nights',
      limitType: 'No Cap',
      notes: 'Business license required',
      link: '/alberta-short-term-rental-regulations'
    },
    {
      name: 'Edmonton',
      province: 'AB',
      entireHomeNights: '365 nights',
      privateRoomNights: '365 nights',
      limitType: 'No Cap',
      notes: 'Business license required',
      link: '/alberta-short-term-rental-regulations'
    },
    {
      name: 'Toronto',
      province: 'ON',
      entireHomeNights: '180 nights',
      privateRoomNights: '365 nights',
      limitType: 'Calendar Cap',
      notes: 'Principal residence required',
      link: '/toronto-str-regulations'
    }
  ];

  const handleRowClick = (city: typeof cities[0]) => {
    if (city.link && window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(city.link);
    }
  };

  const toggleExpandAll = () => {
    setAllExpanded(!allExpanded);
    setExpandedRow(null);
  };

  const isCardExpanded = (index: number) => {
    return allExpanded || expandedRow === index;
  };

  const toggleCard = (index: number) => {
    if (allExpanded) {
      setAllExpanded(false);
      setExpandedRow(index === expandedRow ? null : index);
    } else {
      setExpandedRow(expandedRow === index ? null : index);
    }
  };

  return (
    <section id="eligibility-check" className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            2026 Quick Eligibility Check
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Compare maximum rental nights across major Canadian cities. Click any city to see full details and local strategies.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <div className="max-h-[314px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-[#882629] text-white sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">City</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Province</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Max Nights (Entire Home)</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Max Nights (Private Room)</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Type of Limit</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {cities.map((city, index) => (
                    <tr 
                      key={index}
                      onClick={() => handleRowClick(city)}
                      className="hover:bg-[#F0F0F0]/50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-[#505A5A] whitespace-nowrap">
                        {city.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70 whitespace-nowrap">
                        {city.province}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70 whitespace-nowrap">
                        {city.entireHomeNights}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70 whitespace-nowrap">
                        {city.privateRoomNights}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70 whitespace-nowrap">
                        {city.limitType}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/60 max-w-xs">
                        {city.notes}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden">
          <button
            onClick={toggleExpandAll}
            className="w-full flex items-center justify-center gap-2 bg-[#882629] text-white px-5 py-3 rounded-xl text-sm font-medium mb-4 cursor-pointer whitespace-nowrap hover:bg-[#6E1F22] transition-colors"
          >
            <i className={`ri-${allExpanded ? 'contract' : 'expand'}-up-down-line w-4 h-4 flex items-center justify-center`}></i>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>

          <div className="space-y-3">
            {cities.map((city, index) => {
              const expanded = isCardExpanded(index);
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  {/* Card Header - always visible */}
                  <div
                    onClick={() => toggleCard(index)}
                    className="flex items-center justify-between px-4 py-3.5 cursor-pointer hover:bg-[#F0F0F0]/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-[#505A5A]">{city.name}</span>
                      <span className="text-xs text-[#505A5A]/50 bg-[#F0F0F0] px-2 py-0.5 rounded-full">{city.province}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-[#882629]">{city.entireHomeNights}</span>
                      <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-[#505A5A]/50 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}></i>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  <div
                    className={`transition-all duration-200 ease-in-out overflow-hidden ${expanded ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 pb-4 pt-1 border-t border-[#F0F0F0]">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#505A5A]/40 mb-0.5">Entire Home</p>
                          <p className="text-sm font-medium text-[#505A5A]">{city.entireHomeNights}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#505A5A]/40 mb-0.5">Private Room</p>
                          <p className="text-sm font-medium text-[#505A5A]">{city.privateRoomNights}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#505A5A]/40 mb-0.5">Limit Type</p>
                          <p className="text-sm font-medium text-[#505A5A]">{city.limitType}</p>
                        </div>
                      </div>
                      <p className="text-xs text-[#505A5A]/60 leading-relaxed mb-3">{city.notes}</p>
                      <a
                        href={city.link}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRowClick(city);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#882629] hover:text-[#6E1F22] transition-colors cursor-pointer"
                      >
                        View full details
                        <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-[#505A5A]/60 text-sm mb-4">
            Rules change frequently. Get personalized guidance for your property.
          </p>
          <button 
            onClick={() => {
              window.REACT_APP_NAVIGATE('/book-call');
            }}
            className="bg-[#882629] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl inline-flex items-center gap-3"
          >
            <i className="ri-calendar-check-line text-lg"></i>
            Book a Free Assessment
          </button>
        </div>
      </div>
    </section>
  );
}
